const DEFAULT_MIN_ROW_HEIGHT = 90;
const DEFAULT_GAP = 2;

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function buildRows({
  items,
  width,
  gap,
  targetRowHeight,
  maxRows,
  maxItemsPerRow,
}) {
  const rows = [];
  let row = [];
  let sumAR = 0;

  const wouldOverflow = (nextSumAR, nextCount) => {
    const estWidth = nextSumAR * targetRowHeight + gap * (nextCount - 1);
    return estWidth > width;
  };

  for (const item of items) {
    if (row.length === 0) {
      row = [item];
      sumAR = item.aspectRatio;
      continue;
    }

    const nextCount = row.length + 1;
    const nextSumAR = sumAR + item.aspectRatio;

    const exceedsMaxItems = maxItemsPerRow && nextCount > maxItemsPerRow;

    if (exceedsMaxItems || wouldOverflow(nextSumAR, nextCount)) {
      rows.push({ items: row, sumAR });
      if (maxRows && rows.length >= maxRows) {
        return { ok: false };
      }
      row = [item];
      sumAR = item.aspectRatio;
    } else {
      row.push(item);
      sumAR = nextSumAR;
    }
  }

  if (row.length) rows.push({ items: row, sumAR });
  if (maxRows && rows.length > maxRows) return { ok: false };

  for (let i = rows.length - 1; i > 0; i--) {
    const cur = rows[i];
    const prev = rows[i - 1];
    if (cur.items.length === 1 && prev.items.length > 1) {
      // Move one item from prev to the front of cur
      cur.items.unshift(prev.items.pop());
      prev.sumAR = prev.items.reduce((a, it) => a + it.aspectRatio, 0);
      cur.sumAR = cur.items.reduce((a, it) => a + it.aspectRatio, 0);
    }
  }

  if (rows.length >= 3 && items.length >= 10) {
    while (
      rows.length >= 2 &&
      rows[rows.length - 1].items.length < 3 &&
      rows[rows.length - 2].items.length > 3
    ) {
      const last = rows[rows.length - 1];
      const prev = rows[rows.length - 2];
      last.items.unshift(prev.items.pop());
      prev.sumAR = prev.items.reduce((a, it) => a + it.aspectRatio, 0);
      last.sumAR = last.items.reduce((a, it) => a + it.aspectRatio, 0);
    }
  }

  if (maxItemsPerRow && rows.length >= 2) {
    const desiredLastRowMin = Math.max(3, Math.ceil(maxItemsPerRow * 0.75));
    const last = rows[rows.length - 1];

    while (last.items.length < desiredLastRowMin) {
      let donorIndex = -1;
      for (let i = rows.length - 2; i >= 0; i--) {
        if (rows[i].items.length > desiredLastRowMin) {
          donorIndex = i;
          break;
        }
      }
      if (donorIndex === -1) break;

      const donor = rows[donorIndex];
      last.items.unshift(donor.items.pop());
      donor.sumAR = donor.items.reduce((a, it) => a + it.aspectRatio, 0);
      last.sumAR = last.items.reduce((a, it) => a + it.aspectRatio, 0);
    }
  }

  let totalHeight = 0;
  const computedRows = rows.map((r) => {
    const n = r.items.length;

    const rowHeight = (width - gap * (n - 1)) / r.sumAR;
    totalHeight += rowHeight;
    return { ...r, rowHeight };
  });

  totalHeight += gap * (computedRows.length - 1);

  return { ok: true, rows: computedRows, totalHeight };
}

/**
 * Computes absolute rectangles for each participant tile.
 *
 * @param {Object} params
 * @param {{id: string, aspectRatio: number}[]} params.participants
 * @param {number} params.containerWidth
 * @param {number} params.containerHeight
 * @param {number=} params.gap
 * @param {number=} params.padding
 * @param {number=} params.minRowHeight
 * @param {number=} params.maxRows
 * @param {number=} params.maxItemsPerRow
 * @returns {{items: Array<{id: string, x: number, y: number, width: number, height: number, rowIndex: number, rowSize: number}>, contentHeight: number}}
 */
export function computeDynamicLayout({
  participants,
  containerWidth,
  containerHeight,
  gap = DEFAULT_GAP,
  padding = 0,
  minRowHeight = DEFAULT_MIN_ROW_HEIGHT,
  maxRows = 7,
  maxItemsPerRow,
}) {
  const innerWidth = Math.max(0, (containerWidth || 0) - padding * 2);
  const innerHeight = Math.max(0, (containerHeight || 0) - padding * 2);

  const items = (participants || [])
    .filter((p) => p && p.id)
    .map((p) => ({
      id: p.id,
      aspectRatio: clamp(Number(p.aspectRatio || 16 / 9), 0.25, 4),
    }));

  if (innerWidth <= 0 || innerHeight <= 0 || items.length === 0) {
    return { items: [], contentHeight: 0 };
  }

  // Single participant: fit within box keeping aspect ratio, centered.
  if (items.length === 1) {
    const ar = items[0].aspectRatio;
    let w = innerWidth;
    let h = w / ar;
    if (h > innerHeight) {
      h = innerHeight;
      w = h * ar;
    }

    const x = padding + (innerWidth - w) / 2;
    const y = padding + (innerHeight - h) / 2;

    return {
      items: [
        {
          id: items[0].id,
          x,
          y,
          width: w,
          height: h,
          rowIndex: 0,
          rowSize: 1,
        },
      ],
      contentHeight: h,
    };
  }

  if (items.length === 2) {
    const ar0 = items[0].aspectRatio;
    const ar1 = items[1].aspectRatio;
    const usableW = Math.max(0, innerWidth - gap);

    // Make first participant larger (60% of width) when there are only 2
    const firstParticipantWidthRatio = 0.6;
    const w0Target = usableW * firstParticipantWidthRatio;
    const w1Target = usableW * (1 - firstParticipantWidthRatio);

    // Calculate heights based on aspect ratios
    const h0 = w0Target / ar0;
    const h1 = w1Target / ar1;

    // Use the smaller height to fit both, then adjust widths
    const rowH = Math.min(h0, h1, innerHeight);
    const w0 = rowH * ar0;
    const w1 = Math.min(rowH * ar1, usableW - w0 - gap);

    // Center the layout
    const totalW = w0 + gap + w1;
    const xStart = padding + (innerWidth - totalW) / 2;
    const y = padding + (innerHeight - rowH) / 2;

    return {
      items: [
        {
          id: items[0].id,
          x: xStart,
          y,
          width: w0,
          height: rowH,
          rowIndex: 0,
          rowSize: 2,
        },
        {
          id: items[1].id,
          x: xStart + w0 + gap,
          y,
          width: w1,
          height: rowH,
          rowIndex: 0,
          rowSize: 2,
        },
      ],
      contentHeight: rowH,
    };
  }

  if (items.length === 3) {
    const arTop = items[0].aspectRatio;
    const ar1 = items[1].aspectRatio;
    const ar2 = items[2].aspectRatio;

    const usableW = Math.max(0, innerWidth - gap);

    const maxRowHByHeight = Math.max(0, (innerHeight - gap) / 2);
    const maxRowHByTop = innerWidth / arTop;
    const maxRowHByBottom = usableW / (ar1 + ar2);
    const rowH = Math.min(maxRowHByHeight, maxRowHByTop, maxRowHByBottom);

    const totalH = rowH * 2 + gap;
    const yStart = padding + Math.max(0, (innerHeight - totalH) / 2);

    const topW = rowH * arTop;
    const topX = padding + (innerWidth - topW) / 2;

    const w1 = rowH * ar1;
    const w2 = rowH * ar2;
    const rowW = w1 + w2 + gap;
    const bottomX = padding + (innerWidth - rowW) / 2;
    const bottomY = yStart + rowH + gap;

    return {
      items: [
        {
          id: items[0].id,
          x: topX,
          y: yStart,
          width: topW,
          height: rowH,
          rowIndex: 0,
          rowSize: 1,
        },
        {
          id: items[1].id,
          x: bottomX,
          y: bottomY,
          width: w1,
          height: rowH,
          rowIndex: 1,
          rowSize: 2,
        },
        {
          id: items[2].id,
          x: bottomX + w1 + gap,
          y: bottomY,
          width: w2,
          height: rowH,
          rowIndex: 1,
          rowSize: 2,
        },
      ],
      contentHeight: totalH,
    };
  }

  if (items.length === 4) {
    const ar0 = items[0].aspectRatio;
    const ar1 = items[1].aspectRatio;
    const ar2 = items[2].aspectRatio;
    const ar3 = items[3].aspectRatio;

    const usableW = Math.max(0, innerWidth - gap);

    const maxRowHByHeight = Math.max(0, (innerHeight - gap) / 2);
    const maxRowHByTop = usableW / (ar0 + ar1);
    const maxRowHByBottom = usableW / (ar2 + ar3);
    const rowH = Math.min(maxRowHByHeight, maxRowHByTop, maxRowHByBottom);

    const totalH = rowH * 2 + gap;
    const yStart = padding + Math.max(0, (innerHeight - totalH) / 2);

    const w0 = rowH * ar0;
    const w1 = rowH * ar1;
    const topRowW = w0 + w1 + gap;
    const topX = padding + (innerWidth - topRowW) / 2;

    const w2 = rowH * ar2;
    const w3 = rowH * ar3;
    const bottomRowW = w2 + w3 + gap;
    const bottomX = padding + (innerWidth - bottomRowW) / 2;

    return {
      items: [
        {
          id: items[0].id,
          x: topX,
          y: yStart,
          width: w0,
          height: rowH,
          rowIndex: 0,
          rowSize: 2,
        },
        {
          id: items[1].id,
          x: topX + w0 + gap,
          y: yStart,
          width: w1,
          height: rowH,
          rowIndex: 0,
          rowSize: 2,
        },
        {
          id: items[2].id,
          x: bottomX,
          y: yStart + rowH + gap,
          width: w2,
          height: rowH,
          rowIndex: 1,
          rowSize: 2,
        },
        {
          id: items[3].id,
          x: bottomX + w2 + gap,
          y: yStart + rowH + gap,
          width: w3,
          height: rowH,
          rowIndex: 1,
          rowSize: 2,
        },
      ],
      contentHeight: totalH,
    };
  }

  // Odd-count “balanced” two-row layout (requested):
  // 3 => 1 top, 2 bottom (already handled above)
  // 5 => 2 top, 3 bottom
  // 7 => 3 top, 4 bottom
  if (items.length === 5 || items.length === 7) {
    const n = items.length;
    const topCount = Math.floor(n / 2);
    const bottomCount = n - topCount; // ceil(n/2)

    const topItems = items.slice(0, topCount);
    const bottomItems = items.slice(topCount);

    const sumTop = topItems.reduce((a, it) => a + it.aspectRatio, 0);
    const sumBottom = bottomItems.reduce((a, it) => a + it.aspectRatio, 0);

    const maxHByHeight = Math.max(0, (innerHeight - gap) / 2);
    const maxHByTop =
      sumTop > 0
        ? (innerWidth - gap * (topCount - 1)) / Math.max(0.001, sumTop)
        : 0;
    const maxHByBottom =
      sumBottom > 0
        ? (innerWidth - gap * (bottomCount - 1)) / Math.max(0.001, sumBottom)
        : 0;

    const rowH = Math.max(0, Math.min(maxHByHeight, maxHByTop, maxHByBottom));
    const totalH = rowH * 2 + gap;
    const yStart = padding + Math.max(0, (innerHeight - totalH) / 2);

    const widthsTop = topItems.map((it) => rowH * it.aspectRatio);
    const topRowW = widthsTop.reduce((a, b) => a + b, 0) + gap * (topCount - 1);
    const topX = padding + (innerWidth - topRowW) / 2;

    const widthsBottom = bottomItems.map((it) => rowH * it.aspectRatio);
    const bottomRowW =
      widthsBottom.reduce((a, b) => a + b, 0) + gap * (bottomCount - 1);
    const bottomX = padding + (innerWidth - bottomRowW) / 2;

    const out = [];

    // Top row
    let x = topX;
    topItems.forEach((it, idx) => {
      const w = widthsTop[idx];
      out.push({
        id: it.id,
        x,
        y: yStart,
        width: w,
        height: rowH,
        rowIndex: 0,
        rowSize: topCount,
      });
      x += w + gap;
    });

    // Bottom row
    x = bottomX;
    const bottomY = yStart + rowH + gap;
    bottomItems.forEach((it, idx) => {
      const w = widthsBottom[idx];
      out.push({
        id: it.id,
        x,
        y: bottomY,
        width: w,
        height: rowH,
        rowIndex: 1,
        rowSize: bottomCount,
      });
      x += w + gap;
    });

    return { items: out, contentHeight: totalH };
  }

  let effectiveMaxItemsPerRow =
    typeof maxItemsPerRow === "number" ? maxItemsPerRow : null;
  const hasMobileParticipant = items.some((it) => it.aspectRatio < 1);
  if (!effectiveMaxItemsPerRow && items.length >= 5 && !hasMobileParticipant) {
    const n = items.length;
    const avgAR =
      items.reduce((a, it) => a + it.aspectRatio, 0) / Math.max(1, n);

    const minCols = Math.max(2, Math.ceil(n / Math.max(1, maxRows)));
    const maxCols = Math.min(n - 1, 7);

    const scoreCols = (cols) => {
      const rows = Math.ceil(n / cols);
      const remainder = n % cols;

      const badRemainder =
        remainder === 1 || (remainder === 2 && n >= 10 && rows >= 3);
      if (badRemainder) return -Infinity;

      const hByWidth =
        (innerWidth - gap * (cols - 1)) / Math.max(0.001, cols * avgAR);

      const hByHeight = (innerHeight - gap * (rows - 1)) / Math.max(1, rows);

      if (hByHeight <= 0) return -Infinity;

      const underfillRatio = hByHeight / Math.max(0.001, hByWidth);
      const underfillPenalty =
        underfillRatio < 1 ? (1 - underfillRatio) * 50000 : 0;

      const expectedRowH = Math.min(hByWidth, hByHeight);

      const lastFillFrac = remainder === 0 ? 1 : remainder / cols;
      const shortLastRowPenalty =
        remainder === 0 ? 0 : Math.max(0, 0.75 - lastFillFrac) * 30000;

      return (
        expectedRowH * 1000 - underfillPenalty - shortLastRowPenalty - rows * 50
      );
    };

    let bestCols = null;
    let bestScore = -Infinity;
    for (let cols = minCols; cols <= maxCols; cols++) {
      const s = scoreCols(cols);
      if (s > bestScore) {
        bestScore = s;
        bestCols = cols;
      }
    }

    if (!bestCols) bestCols = Math.min(Math.max(minCols, 4), maxCols);

    effectiveMaxItemsPerRow = bestCols;
  }

  const loInit = Math.min(minRowHeight, innerHeight);
  let lo = loInit;
  let hi = innerHeight;
  let best = null;

  for (let i = 0; i < 24; i++) {
    const mid = (lo + hi) / 2;
    const res = buildRows({
      items,
      width: innerWidth,
      gap,
      targetRowHeight: mid,
      maxRows,
      maxItemsPerRow: effectiveMaxItemsPerRow,
    });

    if (res.ok && res.totalHeight <= innerHeight) {
      best = res;
      lo = mid;
    } else {
      hi = mid;
    }
  }

  if (!best) {
    best = buildRows({
      items,
      width: innerWidth,
      gap,
      targetRowHeight: loInit,
      maxRows,
      maxItemsPerRow: effectiveMaxItemsPerRow,
    });
    if (!best.ok || !best.rows) return { items: [], contentHeight: 0 };
  }

  const contentHeight = best.totalHeight;

  const scaleFactor =
    contentHeight > innerHeight && contentHeight > 0
      ? innerHeight / contentHeight
      : 1;

  const scaledContentHeight = contentHeight * scaleFactor;
  const yOffset =
    padding + Math.max(0, (innerHeight - scaledContentHeight) / 2);

  const out = [];
  let y = yOffset;

  best.rows.forEach((row, rowIndex) => {
    const n = row.items.length;
    const rowSize = n;
    const actualRowHeight = row.rowHeight * scaleFactor;

    const sumAspectRatios = row.items.reduce(
      (sum, it) => sum + it.aspectRatio,
      0
    );
    const targetWidthsSum = innerWidth - gap * (n - 1);

    const adjustedRowHeight =
      sumAspectRatios > 0 ? targetWidthsSum / sumAspectRatios : actualRowHeight;

    const finalRowHeight =
      Math.abs(adjustedRowHeight - actualRowHeight) < actualRowHeight * 0.2
        ? adjustedRowHeight
        : actualRowHeight;

    const widths = row.items.map((it) => finalRowHeight * it.aspectRatio);
    const sumWidths = widths.reduce((a, b) => a + b, 0);
    const drift = targetWidthsSum - sumWidths;

    if (Math.abs(drift) > 0.1 && sumWidths > 0) {
      const scaleFactor = targetWidthsSum / sumWidths;
      for (let i = 0; i < widths.length; i++) {
        widths[i] = widths[i] * scaleFactor;
      }
    } else if (Math.abs(drift) > 0.1 && n > 0) {
      const avgWidth = targetWidthsSum / n;
      for (let i = 0; i < widths.length; i++) {
        widths[i] = avgWidth;
      }
    }

    let x = padding;

    row.items.forEach((it, idx) => {
      const w = widths[idx];
      const h = finalRowHeight;
      out.push({
        id: it.id,
        x,
        y,
        width: w,
        height: h,
        rowIndex,
        rowSize,
      });
      x += w + gap;
    });

    y += finalRowHeight + gap;
  });

  return { items: out, contentHeight: scaledContentHeight };
}


export function computeOptimalGridLayout({
  participants,
  containerWidth,
  containerHeight,
  gap = DEFAULT_GAP,
  padding = 0,
  minRowHeight = DEFAULT_MIN_ROW_HEIGHT,
  maxRows = 7,
  maxItemsPerRow,
  targetAspectRatio = 16 / 9,
}) {
  const initialLayout = computeDynamicLayout({
    participants,
    containerWidth,
    containerHeight,
    gap,
    padding,
    minRowHeight,
    maxRows,
    maxItemsPerRow,
  });

  const rowsMap = new Map();
  initialLayout.items.forEach((item) => {
    if (!rowsMap.has(item.rowIndex)) {
      rowsMap.set(item.rowIndex, []);
    }
    rowsMap.get(item.rowIndex).push(item);
  });

  let maxTotalPadding = 0;
  rowsMap.forEach((rowItems) => {
    const rowHeight = rowItems[0].height;
    const idealTileWidth = rowHeight * targetAspectRatio;
    const idealRowWidth =
      rowItems.length * idealTileWidth + (rowItems.length - 1) * gap;
    const actualRowWidth =
      rowItems.reduce((sum, item) => sum + item.width, 0) +
      (rowItems.length - 1) * gap;
    const totalPaddingNeeded = actualRowWidth - idealRowWidth;

    if (totalPaddingNeeded > maxTotalPadding) {
      maxTotalPadding = totalPaddingNeeded;
    }
  });

  let optimalGridWidth = Math.max(0, containerWidth - maxTotalPadding);
  let sidePadding = maxTotalPadding / 2;

  let optimalLayout = computeDynamicLayout({
    participants,
    containerWidth: optimalGridWidth,
    containerHeight,
    gap,
    padding,
    minRowHeight,
    maxRows,
    maxItemsPerRow,
  });

  let optimalLayoutItems = optimalLayout.items;
  let optimalContentHeight = optimalLayout.contentHeight;

  const innerHeight = containerHeight - padding * 2;
  let maxItemBottom = 0;
  if (optimalLayoutItems.length > 0) {
    maxItemBottom = Math.max(
      ...optimalLayoutItems.map((item) => item.y + item.height)
    );
  }

  if (
    maxItemBottom > containerHeight &&
    optimalGridWidth > containerWidth * 0.4
  ) {
    let attempts = 0;
    let currentWidth = optimalGridWidth;
    let currentItems = optimalLayoutItems;
    let currentMaxBottom = maxItemBottom;

    while (
      currentMaxBottom > containerHeight &&
      attempts < 10 &&
      currentWidth > containerWidth * 0.4
    ) {
      currentWidth = currentWidth * 0.95;
      const recalculated = computeDynamicLayout({
        participants,
        containerWidth: currentWidth,
        containerHeight,
        gap,
        padding,
        minRowHeight,
        maxRows,
        maxItemsPerRow,
      });

      currentItems = recalculated.items;
      if (currentItems.length > 0) {
        currentMaxBottom = Math.max(
          ...currentItems.map((item) => item.y + item.height)
        );
      } else {
        break;
      }

      attempts++;
    }

    if (currentMaxBottom <= containerHeight) {
      optimalGridWidth = currentWidth;
      optimalLayoutItems = currentItems;
      optimalContentHeight =
        currentItems.length > 0
          ? Math.max(...currentItems.map((item) => item.y + item.height))
          : optimalContentHeight;
      sidePadding = (containerWidth - optimalGridWidth) / 2;
    }
  }

  let finalMaxBottom = 0;
  if (optimalLayoutItems.length > 0) {
    finalMaxBottom = Math.max(
      ...optimalLayoutItems.map((item) => item.y + item.height)
    );
  }

  if (finalMaxBottom > containerHeight) {
    const widthReductionNeeded =
      ((finalMaxBottom - containerHeight) / containerHeight) * optimalGridWidth;
    optimalGridWidth = Math.max(
      containerWidth * 0.3,
      optimalGridWidth - widthReductionNeeded
    );
    sidePadding = (containerWidth - optimalGridWidth) / 2;

    const finalRecalc = computeDynamicLayout({
      participants,
      containerWidth: optimalGridWidth,
      containerHeight,
      gap,
      padding,
      minRowHeight,
      maxRows,
      maxItemsPerRow,
    });

    optimalLayoutItems = finalRecalc.items;
    optimalContentHeight = finalRecalc.contentHeight;
  }

  return {
    items: optimalLayoutItems,
    optimalGridWidth,
    sidePadding,
    contentHeight: optimalContentHeight,
  };
}
